import React, { useState, useEffect } from 'react';
import { X, Zap, ShieldCheck, Loader2 } from 'lucide-react';

/**
 * PrepChargeConfirmModal — asks the applicant to confirm before we debit Mu for
 * a single interview prep question. Confirmation is REQUIRED before every
 * charged question so users always know when their balance moves.
 *
 * Props:
 *   isOpen, onClose, onConfirm (async), amountMu, balance
 */
export default function PrepChargeConfirmModal({ isOpen, onClose, onConfirm, amountMu, balance }) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) { setError(''); setConfirming(false); }
  }, [isOpen]);

  if (!isOpen) return null;

  const insufficient = typeof balance === 'number' && balance < amountMu;

  const handleConfirm = async () => {
    if (insufficient) return;
    setConfirming(true);
    setError('');
    try {
      await onConfirm();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Could not charge for this question');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
         data-testid="prep-charge-modal">
      <div className="w-full max-w-sm bg-white dark:bg-[#0f0f12] border border-slate-200 dark:border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-zinc-800">
          <h3 className="text-slate-900 dark:text-white font-semibold flex items-center gap-2 text-sm">
            <ShieldCheck className="w-4 h-4 text-amber-500" /> Confirm charge
          </h3>
          <button onClick={onClose} className="text-slate-500 dark:text-zinc-500 hover:text-slate-900 dark:hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <p className="text-slate-700 dark:text-zinc-300 text-sm">
            The next interview question will cost{' '}
            <span className="font-semibold text-amber-600 dark:text-amber-400 inline-flex items-center gap-1">
              <Zap className="w-3 h-3" /> {amountMu} Mu
            </span>.
          </p>
          <div className="rounded-lg bg-slate-50 dark:bg-black border border-slate-200 dark:border-zinc-800 p-3 text-xs flex items-center justify-between">
            <span className="text-slate-500 dark:text-zinc-500">Current balance</span>
            <span className={insufficient ? 'text-red-500 font-medium' : 'text-slate-900 dark:text-white font-medium'}>
              {balance ?? '—'} Mu
            </span>
          </div>
          {insufficient && (
            <p className="text-[11px] text-red-500 dark:text-red-400" data-testid="prep-charge-insufficient">
              Not enough Mu. Please recharge from your wallet first.
            </p>
          )}
          {error && <p className="text-[11px] text-red-500 dark:text-red-400" data-testid="prep-charge-error">{error}</p>}

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg bg-slate-100 dark:bg-zinc-900 hover:bg-slate-200 dark:hover:bg-zinc-800 text-slate-700 dark:text-zinc-300 text-sm font-medium"
              data-testid="prep-charge-cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={confirming || insufficient}
              className="flex-1 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-medium flex items-center justify-center gap-1"
              data-testid="prep-charge-confirm"
            >
              {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {confirming ? 'Charging…' : `Charge ${amountMu} Mu`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
